import Decimal from 'decimal.js';
import {
    AssetType,
    FormatOptions,
    FormattedPrice,
    MaxTradeResult,
} from '../../types/formatting';
import {
    DEFAULT_ASSET_DECIMALS,
    COMPACT_NOTATION_THRESHOLDS,
    MINIMUM_DISPLAY_VALUES,
} from './constants';

/**
 * Configure Decimal.js for financial precision
 */
Decimal.set({
    precision: 28,
    rounding: Decimal.ROUND_HALF_UP,
    maxExponent: 9e15,
    minExponent: -9e15,
});

const SMART_DECIMAL_RULES = {
    // For quantities (token amounts)
    quantity: [
        { threshold: 1000, decimals: 2 },      // 23,761,999.81
        { threshold: 100, decimals: 2 },       // 151.42
        { threshold: 10, decimals: 3 },        // 15.142
        { threshold: 1, decimals: 4 },         // 3.1326
        { threshold: 0.1, decimals: 5 },       // 0.07192
        { threshold: 0.01, decimals: 6 },      // 0.007192
        { threshold: 0.001, decimals: 7 },     // 0.0007192
        { threshold: 0.0001, decimals: 8 },    // 0.00007192
        { threshold: 0, significantDigits: 4 }, // For very small: show 4 significant digits
    ],
    // For prices (asset value in currency)
    price: [
        { threshold: 1000, decimals: 2 },      // $1,234.56
        { threshold: 1, decimals: 2 },         // $1.23
        { threshold: 0.01, decimals: 4 },      // $0.0123
        { threshold: 0.0001, decimals: 6 },    // $0.000123
        { threshold: 0.00000001, decimals: 8 }, // $0.00000123
        { threshold: 0, significantDigits: 4 }, // For very small: show 4 significant digits
    ],
    // For amounts (total values in currency)
    amount: [
        { threshold: 1000, decimals: 2 },      // $23,761,999.81
        { threshold: 1, decimals: 2 },         // $1.23
        { threshold: 0.01, decimals: 4 },      // $0.0123
        { threshold: 0.0001, decimals: 6 },    // $0.000123
        { threshold: 0, significantDigits: 4 }, // For very small: show 4 significant digits
    ],
};

/**
 * Safe value parsing with error handling
 */
export const parseDecimal = (value: any): Decimal => {
    try {
        if (value instanceof Decimal) {
            return value;
        }
        if (typeof value === 'string') {
            const cleaned = value.trim();
            if (!cleaned || cleaned === '' || cleaned === '-') {
                return new Decimal(0);
            }
            return new Decimal(cleaned);
        }
        if (typeof value === 'number') {
            if (!isFinite(value)) {
                return new Decimal(0);
            }
            return new Decimal(value);
        }
        return new Decimal(0);
    } catch (error) {
        console.warn(`Failed to parse decimal value: ${value}`, error);
        return new Decimal(0);
    }
};

/**
 * Check if a value is too small to display
 */
const isBelowMinimumDisplay = (value: Decimal, minimum: string): boolean => {
    const minDecimal = new Decimal(minimum);
    return value.greaterThan(0) && value.lessThan(minDecimal);
};

/**
 * Remove trailing zeros from string representation
 */
const removeTrailingZeros = (str: string): string => {
    if (!str.includes('.')) {
        return str;
    }
    return str.replace(/\.?0+$/, '');
};

/**
 * Add thousands separator
 */
const addThousandsSeparator = (str: string): string => {
    const parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

/**
 * Get smart decimals based on value magnitude and type
 * This mimics how modern platforms display numbers
 */
export const getSmartDecimals = (
    value: Decimal,
    type: 'quantity' | 'price' | 'amount' = 'quantity'
): { decimals: number; useSignificant: boolean } => {
    const abs = value.abs();
    const rules = SMART_DECIMAL_RULES[type];

    // Find the appropriate rule
    for (const rule of rules) {
        if (abs.greaterThanOrEqualTo(rule.threshold)) {
            if ('significantDigits' in rule) {
                return { decimals: rule.significantDigits, useSignificant: true };
            }
            return { decimals: rule.decimals, useSignificant: false };
        }
    }

    // Default fallback
    return { decimals: 8, useSignificant: false };
};

/**
 * Format number with significant digits
 */
const formatWithSignificantDigits = (
    value: Decimal,
    significantDigits: number
): string => {
    if (value.isZero()) {
        return '0';
    }

    // Use toSignificantDigits for very small numbers
    const significant = value.toSignificantDigits(significantDigits);

    // Convert to string and remove unnecessary trailing zeros
    let str = significant.toString();

    // Handle scientific notation
    if (str.includes('e')) {
        // Convert scientific notation to decimal
        str = significant.toFixed();
    }

    return removeTrailingZeros(str);
};

/**
 * Get appropriate decimal places based on value and asset type
 * DEPRECATED: Use getSmartDecimals instead
 */
const getAdaptiveDecimals = (
    value: Decimal,
    assetType: AssetType
): number => {
    const abs = value.abs();

    // For values >= 1, use default display decimals
    if (abs.greaterThanOrEqualTo(1)) {
        return DEFAULT_ASSET_DECIMALS[assetType];
    }

    // For crypto, allow higher precision for small values
    if (assetType === AssetType.CRYPTO) {
        return 8;
    }

    // For stocks/USD, use standard 2 decimals
    return DEFAULT_ASSET_DECIMALS[assetType];
};

/**
 * Format a price value with comprehensive options
 */
export const formatPrice = (
    value: any,
    options: FormatOptions = {}
): FormattedPrice => {
    const {
        assetType = AssetType.CRYPTO,
        compact = false,
        showSymbol = true,
        currencySymbol = '$',
        decimals: customDecimals,
        forceDecimals = false,
        showSign = false,
        separator = true,
        smartDecimals = true,
        displayType = 'price', // NEW: 'quantity' | 'price' | 'amount'
    } = options as any;

    const decimal = parseDecimal(value);

    // Handle zero
    if (decimal.isZero()) {
        return {
            display: showSymbol ? `${currencySymbol}0.00` : '0.00',
            original: '0',
            decimals: 2,
            isTruncated: false,
            value: '0',
        };
    }

    // Determine decimals to use
    let decimalsToUse: number;
    let useSignificant = false;

    if (customDecimals !== undefined) {
        // Use custom decimals if provided
        decimalsToUse = customDecimals;
    } else if (smartDecimals) {
        // Use smart decimal calculation
        const smartResult = getSmartDecimals(decimal, displayType);
        decimalsToUse = smartResult.decimals;
        useSignificant = smartResult.useSignificant;
    } else {
        // Fallback to adaptive decimals
        decimalsToUse = getAdaptiveDecimals(decimal, assetType);
    }

    const minDisplay =
        MINIMUM_DISPLAY_VALUES[assetType as unknown as keyof typeof MINIMUM_DISPLAY_VALUES];

    // Check if value is below minimum displayable
    if (minDisplay && isBelowMinimumDisplay(decimal, minDisplay)) {
        return {
            display: showSymbol ? `${currencySymbol}<${minDisplay}` : `<${minDisplay}`,
            original: decimal.toString(),
            decimals: decimalsToUse,
            isTruncated: true,
            value: decimal.toString(),
        };
    }

    // Handle compact notation
    let displayValue = decimal;
    let compactSuffix: string | undefined;

    if (compact && decimal.abs().greaterThanOrEqualTo(COMPACT_NOTATION_THRESHOLDS.K)) {
        const absValue = decimal.abs();

        if (absValue.greaterThanOrEqualTo(COMPACT_NOTATION_THRESHOLDS.T)) {
            displayValue = absValue.dividedBy(COMPACT_NOTATION_THRESHOLDS.T);
            compactSuffix = 'T';
        } else if (absValue.greaterThanOrEqualTo(COMPACT_NOTATION_THRESHOLDS.B)) {
            displayValue = absValue.dividedBy(COMPACT_NOTATION_THRESHOLDS.B);
            compactSuffix = 'B';
        } else if (absValue.greaterThanOrEqualTo(COMPACT_NOTATION_THRESHOLDS.M)) {
            displayValue = absValue.dividedBy(COMPACT_NOTATION_THRESHOLDS.M);
            compactSuffix = 'M';
        } else if (absValue.greaterThanOrEqualTo(COMPACT_NOTATION_THRESHOLDS.K)) {
            displayValue = absValue.dividedBy(COMPACT_NOTATION_THRESHOLDS.K);
            compactSuffix = 'K';
        }

        if (decimal.isNegative()) {
            displayValue = displayValue.negated();
        }

        decimalsToUse = 2;
        useSignificant = false;
    }

    let formatted: string;
    let rounded: Decimal;

    if (useSignificant && !compact) {
        // Use significant digits for very small numbers
        formatted = formatWithSignificantDigits(displayValue, decimalsToUse);
        rounded = new Decimal(formatted);
    } else {
        // Round to appropriate decimals
        const factor = new Decimal(10).pow(decimalsToUse);
        rounded = displayValue
            .times(factor)
            .toDecimalPlaces(0, Decimal.ROUND_DOWN)
            .dividedBy(factor);

        // Format the number
        formatted = rounded.toFixed(forceDecimals ? decimalsToUse : undefined);

        // Remove trailing zeros if not forcing decimals
        if (!forceDecimals) {
            formatted = removeTrailingZeros(formatted);
        }
    }

    // Add thousands separator
    if (separator) {
        formatted = addThousandsSeparator(formatted);
    }

    // Add sign
    if (showSign && decimal.greaterThan(0)) {
        formatted = `+${formatted}`;
    }

    // Add suffixes
    if (compactSuffix) {
        formatted = `${formatted}${compactSuffix}`;
    }

    // Add currency symbol — handle sign placement correctly
    const display = showSymbol
        ? formatted.startsWith('-')
            ? `-${currencySymbol}${formatted.slice(1)}`  // -A$733.95
            : formatted.startsWith('+')
                ? `+${currencySymbol}${formatted.slice(1)}`  // +A$0.15
                : `${currencySymbol}${formatted}`             // A$1,000
        : formatted;

    return {
        display,
        original: decimal.toString(),
        decimals: decimalsToUse,
        isTruncated: isBelowMinimumDisplay(decimal, minDisplay || '0'),
        value: rounded.toString(),
        compactSuffix,
    };
};

/**
 * Format balance - always uses 2 decimals for fiat
 * Gets currency symbol and code from Redux slice
 */
export const formatBalance = (
    value: any,
    currencySymbol: string = '$',
    options: Partial<FormatOptions> = {}
): FormattedPrice => {
    return formatPrice(value, {
        assetType: AssetType.USD,
        showSymbol: true,
        currencySymbol,
        decimals: 2, // Always 2 decimals for fiat balance
        smartDecimals: false, // Don't use smart decimals for balance
        displayType: 'amount',
        ...options,
    } as any);
};

/**
 * Format quantity of assets with smart decimal handling
 * Examples:
 *   0.007192 → 0.007192 (show all significant digits)
 *   1.61413083 → 1.6141 (4 decimals)
 *   23,761,999.8095238 → 23,761,999.81 (2 decimals)
 */
export const formatQuantity = (
    value: any,
    assetType: AssetType = AssetType.CRYPTO,
    options: Partial<FormatOptions> = {}
): FormattedPrice => {
    return formatPrice(value, {
        assetType,
        showSymbol: false,
        smartDecimals: true,
        displayType: 'quantity',
        ...options,
    } as any);
};

/**
 * Format asset price with smart decimal handling
 * Examples:
 *   0.0000000008 → 0.0000000008 (show all significant digits)
 *   151515.000000515 → 151,515.00 (2 decimals for large values)
 *   1.23456789 → 1.23 (2 decimals)
 */
export const formatAssetPrice = (
    value: any,
    currencySymbol: string = '$',
    options: Partial<FormatOptions> = {}
): FormattedPrice => {
    return formatPrice(value, {
        assetType: AssetType.CRYPTO,
        showSymbol: true,
        currencySymbol,
        smartDecimals: true,
        displayType: 'price',
        ...options,
    } as any);
};

/**
 * Format amount (quantity × price) with smart decimal handling
 * Examples:
 *   0.00000123 → $0.00000123 (show all significant digits)
 *   1234.56789 → $1,234.57 (2 decimals)
 *   23761999.8095238 → $23,761,999.81 (2 decimals)
 */
export const formatAmount = (
    value: any,
    currencySymbol: string = '$',
    options: Partial<FormatOptions> = {}
): FormattedPrice => {
    return formatPrice(value, {
        assetType: AssetType.USD,
        showSymbol: true,
        currencySymbol,
        smartDecimals: true,
        displayType: 'amount',
        ...options,
    } as any);
};

/**
 * Format very large numbers with compact notation
 */
export const formatCompactNumber = (
    value: any,
    decimals: number = 2
): string => {
    const decimal = parseDecimal(value);
    const result = formatPrice(decimal, {
        compact: true,
        showSymbol: false,
        decimals,
        separator: false,
        smartDecimals: false,
    } as any);
    return result.display;
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (
    current: any,
    previous: any
): string => {
    const currentDec = parseDecimal(current);
    const previousDec = parseDecimal(previous);

    if (previousDec.isZero()) {
        return '0.00%';
    }

    const change = currentDec
        .minus(previousDec)
        .dividedBy(previousDec)
        .times(100);

    return `${change.toFixed(2)}%`;
};

/**
 * Calculate absolute price change
 */
export const calculatePriceChange = (
    current: any,
    previous: any,
    decimals: number = 2
): string => {
    const currentDec = parseDecimal(current);
    const previousDec = parseDecimal(previous);
    const change = currentDec.minus(previousDec);

    const factor = new Decimal(10).pow(decimals);
    const rounded = change
        .times(factor)
        .toDecimalPlaces(0, Decimal.ROUND_DOWN)
        .dividedBy(factor);

    const formatted = rounded.toFixed(decimals);
    const sign = rounded.greaterThan(0) ? '+' : '';

    return `${sign}${formatted}`;
};

/**
 * Calculate max tradeable quantity
 */
export const calculateMaxTrade = (
    balance: any,
    price: any,
    fee: any = 0,
    includesFee: boolean = true
): MaxTradeResult => {
    const balanceDec = parseDecimal(balance);
    const priceDec = parseDecimal(price);
    const feeDec = parseDecimal(fee);

    if (balanceDec.isZero() || priceDec.isZero()) {
        return {
            maxQuantity: '0',
            maxAmount: '0',
            isInsufficientBalance: true,
            includesFee,
        };
    }

    let availableAmount = balanceDec;

    if (includesFee && feeDec.greaterThan(0)) {
        availableAmount = balanceDec.times(new Decimal(1).minus(feeDec));
    }

    const maxQuantity = availableAmount.dividedBy(priceDec);
    const maxAmount = maxQuantity.times(priceDec);

    return {
        maxQuantity: maxQuantity.toFixed(8),
        maxAmount: maxAmount.toFixed(8),
        isInsufficientBalance: availableAmount.isZero(),
        includesFee,
    };
};

/**
 * Validate balance is sufficient for transaction
 */
export const isBalanceSufficient = (
    balance: any,
    amount: any,
    tolerance: number = 0.01
): boolean => {
    const balanceDec = parseDecimal(balance);
    const amountDec = parseDecimal(amount);

    const toleranceAmount = amountDec.times(
        new Decimal(tolerance).dividedBy(100)
    );

    return balanceDec.greaterThanOrEqualTo(amountDec.minus(toleranceAmount));
};

/**
 * Safe comparison of two decimal values
 */
export const compareDecimals = (
    a: any,
    b: any,
    tolerance: number = 1e-8
): number => {
    const aDec = parseDecimal(a);
    const bDec = parseDecimal(b);
    const diff = aDec.minus(bDec).abs();

    if (diff.lessThan(new Decimal(tolerance))) {
        return 0;
    }

    return aDec.greaterThan(bDec) ? 1 : -1;
};

/**
 * Calculate price change amount from percentage and current price
 */
export const calculateChangeAmount = (
    currentPrice: any,
    percentChange: any,
    options: {
        decimals?: number;
        showSign?: boolean;
        showSymbol?: boolean;
        currencySymbol?: string;
        assetType?: AssetType;
    } = {}
): FormattedPrice => {
    const {
        showSign = false,
        showSymbol = false,
        currencySymbol = '$',
        assetType = AssetType.CRYPTO,
    } = options;

    const priceDec = parseDecimal(currentPrice);
    const percentDec = parseDecimal(percentChange);

    if (priceDec.isZero() || percentDec.isNaN()) {
        return formatPrice('0', {
            showSign,
            showSymbol,
            currencySymbol,
            assetType,
            smartDecimals: true,
            displayType: 'price',
        } as any);
    }

    const previousPrice = priceDec.dividedBy(
        new Decimal(1).plus(percentDec.dividedBy(100))
    );

    const changeAmount = priceDec.minus(previousPrice);

    return formatPrice(changeAmount, {
        showSign,
        showSymbol,
        currencySymbol,
        assetType,
        smartDecimals: true,
        displayType: 'price',
    } as any);
};

/**
 * Format a percentage value for display
 */
export const formatPercentage = (
    value: any,
    options: {
        minimumFractionDigits?: number;
        maximumFractionDigits?: number;
        showSign?: boolean;
    } = {}
): string => {
    const {
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
        showSign = false,
    } = options;

    const decimal = parseDecimal(value);

    if (!decimal || decimal.isNaN()) {
        return '0.00%';
    }

    let decimalsToShow = maximumFractionDigits;

    if (
        !decimal.isZero() &&
        decimal.abs().lt(new Decimal(10).pow(-maximumFractionDigits))
    ) {
        const fixed = decimal.abs().toFixed(10);
        const fraction = fixed.split('.')[1] ?? '';

        const firstSignificantDigit = fraction.search(/[1-9]/);

        if (firstSignificantDigit >= 0) {
            decimalsToShow = Math.min(
                firstSignificantDigit + 2,
                10
            );
        }
    }

    const formatted = decimal.toFixed(decimalsToShow);

    const [whole, fraction = ''] = formatted.split('.');

    const result =
        decimalsToShow > 0
            ? `${whole}.${fraction.padEnd(
                Math.min(minimumFractionDigits, decimalsToShow),
                '0'
            )}`
            : whole;

    const sign =
        showSign && decimal.gt(0)
            ? '+'
            : '';

    return `${sign}${result}%`;
};

/**
 * Add/subtract amounts precisely
 */
export const addAmounts = (values: any[]): Decimal => {
    return values.reduce(
        (sum, val) => sum.plus(parseDecimal(val)),
        new Decimal(0)
    );
};

/**
 * Multiply two decimal values
 */
export const multiplyDecimals = (a: any, b: any): Decimal => {
    return parseDecimal(a).times(parseDecimal(b));
};

/**
 * Divide two decimal values
 */
export const divideDecimals = (a: any, b: any): Decimal => {
    const bDec = parseDecimal(b);
    if (bDec.isZero()) {
        console.warn('Division by zero detected');
        return new Decimal(0);
    }
    return parseDecimal(a).dividedBy(bDec);
};

/**
 * Calculate average price
 */
export const calculateAveragePrice = (
    amount: any,
    quantity: any
): Decimal => {
    const amountDec = parseDecimal(amount);
    const quantityDec = parseDecimal(quantity);

    if (quantityDec.isZero()) {
        return new Decimal(0);
    }

    return amountDec.dividedBy(quantityDec);
};